<template>

	<Teleport to='#emergency' :disabled='_options.main.disable_teleport'>

		<component ref='_window' 
			er-window
			:er-type='_options.main.type'
			:class='[ _options.main.size, { "ignore-height" : _options.main.size_ignores_height }, _options.main.extra_classes ]'
			v-if='_ui.window.is_attached'
			:is='_options.form.is_form ? "form" : "div"'
			:wf-form='_options.form.is_form ? "" : null'
			v-bind='$attrs'
			@submit='_options.form.is_form ? _Submit( $event ) : null'>

			<div er-window-handlers>
				<a class='reduce'
					v-if='_ui.header.show_reduce' 
					:title='txc( "Reduce", "emergency" )' 
					@click='_Reduce'>
					<i class='fa-solid fa-external-link fa-rotate-180'></i>
				</a>
				<a class='expand' 
					v-if='_ui.header.show_expand' 
					:title='txc( "Expand", "emergency" )' 
					@click='_Expand'>
					<i class='fa-solid fa-external-link'></i>
				</a>
				<a class='close' 
					v-if='_ui.header.show_close' 
					:title='txc( "Close", "emergency" )' 
					@click='_Close'></a>
			</div>

			<div er-window-title>
				<slot name='title' />
			</div>

			<div er-window-content :class='[ _options.content.align ]'>
				<slot />
			</div>

			<div er-window-actions :class='[ _options.actions.align ]'>
				<button v-if='_ui.tray.show_cancel' wf-button class='cancel'
					@click='!Trigger( "cancel" ) || Close()'>{{ txc( "Cancel", "emergency" ) }}</button>
				<button v-if='_ui.tray.show_apply' wf-button class='apply'
					@click='Trigger( "apply" )'>{{ txc( "Apply", "emergency" ) }}</button>
				<button v-if='_ui.tray.show_accept' wf-button class='accept'
					@click='!Trigger( "accept" ) || Close()'>{{ txc( "Accept", "emergency" ) }}</button>
				<button v-if='_ui.tray.show_ok' wf-button class='ok'
					@click='!Trigger( "ok" ) || Close()'>{{ txc( "Ok", "emergency" ) }}</button>
				<button v-if='_ui.tray.show_no' wf-button class='no'
					@click='!Trigger( "no" ) || Close()'>{{ txc( "No", "emergency" ) }}</button>
				<button v-if='_ui.tray.show_yes' wf-button class='yes'
					@click='!Trigger( "yes" ) || Close()'>{{ txc( "Yes", "emergency" ) }}</button>
			</div>

			<div er-window-timeout v-if='_ui.window.show_timeout' :class='[ _options.timeout.color ]'><span class='count'></span></div>

		</component>

	</Teleport>

	<Teleport to='#emergency'>

		<EmergencyAlert
			ref='_alert'
			v-if='_ui.alert.is_attached'
			@close='_Close_alert'>
			<div v-html='_ui.alert.message' />
		</EmergencyAlert>

	</Teleport>

	<Teleport to='#emergency'>

		<EmergencySpinner
			ref='_spinner'
			v-if='_ui.spinner.is_attached'>
		</EmergencySpinner>

	</Teleport>

</template>

<script setup>

import { computed, ref, nextTick, onMounted } from 'vue';

	import * as Emergency from '@/modules/emergency';
	import EmergencyAlert from '@/components/emergency/EmergencyAlert.vue';
	import EmergencySpinner from '@/components/emergency/EmergencySpinner.vue';
	import { txc } from '@/modules/i18n';

	//

	const exposed_functions =
	{
		Get_element,
		Open,
		Close,
		Focus
	};

	defineExpose( exposed_functions );

	//

	let _timeout_handler = null;

	let _old_size = '';
	let _old_size_ignores_height = false;

	const _window = ref();
	const _alert = ref();
	const _spinner = ref();
	
	const _emit = defineEmits
	( [
		'prepare',
		
		'open',
		
		'expand',
		'reduce',
		
		'close',
		'ok',
		'accept',
		'apply',
		'cancel',
		'yes',
		'no',
		
		'submit',
		
		'destroy'
	] );
	
	const _props = defineProps
	( {
		chain:
		{
			type: String,
			default ()
			{
				return 'modal';
			}
		},
		options:
		{
			type: Object,
			default ()
			{
				return {};
			}
		},
		content:
		{
			type: Object,
			default ()
			{
				return {};
			}
		},
		form:
		{
			type: Object,
			default ()
			{
				return {};
			}
		},
		timeout:
		{
			type: Object,
			default ()
			{
				return {};
			}
		},
		actions:
		{
			type: Object,
			default ()
			{
				return {};
			}
		}
	} );

	const _options = ref
	( {
		main: computed( () =>
		( {
			autofocus: _props?.options?.autofocus
				? _props.options.autofocus
				: 'interactive',
			size: [ 'small', 'medium', 'big', 'full', 'expanded' ].includes( _props?.options?.size )
				? _props.options.size
				: '',
			size_ignores_height: 'size_ignores_height' in _props?.options
				? Boolean( _props.options.size_ignores_height )
				: false,
			can_close: 'can_close' in _props?.options
				? Boolean( _props.options.can_close )
				: true,
			disable_teleport: 'disable_teleport' in _props?.options
				? Boolean( _props.options.disable_teleport )
				: false,
			type: _props?.options?.type
				? _props.options.type
				: '',
			extra_classes: _props?.options?.extra_classes instanceof Array
				? _props?.options?.extra_classes
				: []
		} ) ),
		
		content: computed( () =>
		( {
			align: [ 'left', 'center', 'right' ].includes( _props?.content?.align )
				? _props.content.align
				: null
		} ) ),
		
		form: computed( () =>
		( {
			is_form: 'is_form' in _props?.form
				? Boolean( _props.form.is_form )
				: false,
			show_waiting: 'show_waiting' in _props?.form
				? Boolean( _props.form.show_waiting )
				: true
		} ) ),
		
		timeout: computed( () =>
		( {
			amount: _props?.timeout?.amount
				? Math.floor( Math.abs( parseInt( _props.timeout.amount ) ) )
				: 0,
			color: [ 'success', 'info', 'warning', 'error' ].includes( _props?.timeout?.color )
				? _props.timeout.color
				: 'default'
		} ) ),
		
		actions: computed( () =>
		( {
			header: _props?.actions?.header instanceof Array
				? _props.actions.header
				: [ 'close' ],
			tray: _props?.actions?.tray instanceof Array
				? _props.actions.tray
				: [ 'cancel', 'accept' ],
			align: [ 'left', 'center', 'right' ].includes( _props?.actions?.align )
				? _props.actions.align
				: null
		} ) )
	} );

	const _ui = ref
	( {
		header: computed( () => 
		( {
			show_reduce: _options.value.actions.header.includes( 'expand' ) && _ui.value.window.is_expanded,
			show_expand: _options.value.actions.header.includes( 'expand' ) && ( _options.value.main.size != 'full' ) && !_ui.value.window.is_expanded,
			show_close: _options.value.actions.header.includes( 'close' )
		} ) ),
		
		tray: computed ( () =>
		( {
			show_cancel: _options.value.actions.tray.includes( 'cancel' ),
			show_apply: _options.value.actions.tray.includes( 'apply' ),
			show_accept: _options.value.actions.tray.includes( 'accept' ),
			show_ok: _options.value.actions.tray.includes( 'ok' ),
			show_no: _options.value.actions.tray.includes( 'no' ),
			show_yes: _options.value.actions.tray.includes( 'yes' )
		} ) ),
		
		window:
		{
			is_attached: false,
			is_expanded: false,
			show_timeout: false
		},
		
		alert:
		{
			is_attached: false,
			message: ''
		},
		
		spinner:
		{
			is_attached: false
		}
	} );


	onMounted( () =>
	{
		if ( _options.value.main.size == 'expanded' )
			_Expand();
	} );

	
	//
	
	
	//region Event handlers
	
	
	function _Expand ()
	{
		_old_size = _options.value.main.size;
		_old_size_ignores_height = _options.value.main.size_ignores_height;
		
		_options.value.main.size = 'max';
		_options.value.main.size_ignores_height = false;
		_ui.value.window.is_expanded = true;
		
		_emit( 'expand', _window );
	}
	
	
	function _Reduce ()
	{
		_options.value.main.size = _old_size;
		_options.value.main.size_ignores_height = _old_size_ignores_height;
		_ui.value.window.is_expanded = false;
		
		_emit( 'reduce', _window );
	}
	
	
	function _Close ()
	{
		// Intelligently emit "cancel" or "no" events
		if ( _options.value.actions.tray.includes( 'cancel' ) && !Trigger( 'cancel' ) )
			return;
		else if ( _options.value.actions.tray.includes( 'no' ) && !Trigger( 'no' ) )
			return;
		
		Close();
	}
	
	
	function _Ok ()
	{
		if ( !Trigger( 'ok' ) )
			return;
		
		Close();
	}
	
	
	function _Accept ()
	{
		if ( !Trigger( 'accept' ) )
			return;
		
		Close();
	}
	
	
	function _Apply ()
	{
		Trigger( 'apply' );
	}
	
	
	function _Cancel ()
	{
		if ( !Trigger( 'cancel' ) )
			return;
		
		Close();
	}
	
	
	function _Yes ()
	{
		if ( !Trigger( 'yes' ) )
			return;
		
		Close();
	}
	
	
	function _No ()
	{
		if ( !Trigger( 'no' ) )
			return;
		
		Close();
	}
	
	
	async function _Submit ( event )
	{
		event.preventDefault();
		
		if ( _options.value.form.is_form )
		{
			await Show_spinner();
			
			let result = await Trigger( 'submit' );
			
			Hide_spinner();
			
			if ( !result )
				return;
		}
		
		await Close();
	}


	function _Close_alert ()
	{
		setTimeout( () =>
		{
			_ui.value.alert.message = null;
			_ui.value.alert.is_attached = false;
		}, 350 );
	}

	
	// endregion
	
	
	//region Public methods
	
	
	async function Open ()
	{
		// Prepare the window and trigger the prepare event
		_ui.value.window.is_attached = true;
		
		await nextTick();
		
		if ( !( await Trigger( 'prepare' ) ) )
		{
			_ui.value.window.is_attached = false;
			return;
		}
		
		// Open the window and emit the open event
		let chain_parts = _props.chain.split( ':' );
		
		Emergency.Open( _window.value, exposed_functions, chain_parts.shift(), chain_parts );
		
		_emit( 'open', _window );
		
		Autofocus();
		
		if ( _options.value.timeout.amount > 0 )
			await Start_timeout();
	}
	
	
	async function Close ( force = false )
	{
		Cancel_timeout();
		
		if ( force || _options.value.main.can_close )
		{
			// Emit the close event and close the window
			if ( !await Trigger( 'close' ) )
				return;
			
			Emergency.Close( _window.value );
			
			if ( _options.value.main.size == 'max' )
				_Reduce();
			
			// Emit the destroy event
			_emit( 'destroy', _window )
			
			// Unattach the window from the DOM after the animation (0.3s)
			setTimeout( () =>
			{
				_ui.value.window.is_attached = false;
			}, 350 );
		}
	}
	
	
	function Focus ( selector )
	{
		let css_query = selector;
		
		switch ( true )
		{
			case ( selector == 'cancel' ) && _ui.value.tray.show_cancel:
				css_query = '[er-window-actions] .cancel';
				break;
			
			case ( selector == 'apply' ) && _ui.value.tray.show_apply:
				css_query = '[er-window-actions] .apply';
				break;
			
			case ( selector == 'accept' ) && _ui.value.tray.show_accept:
				css_query = '[er-window-actions] .accept';
				break;
			
			case ( selector == 'ok' ) && _ui.value.tray.show_ok:
				css_query = '[er-window-actions] .ok';
				break;
			
			case ( selector ==  'no' ) && _ui.value.tray.show_no:
				css_query = '[er-window-actions] .no';
				break;
			
			case ( selector == 'yes' ) && _ui.value.tray.show_yes:
				css_query = '[er-window-actions] .yes';
				break;
			
			case ( selector == 'field' ):
				css_query = '[er-window-content] input:not([type~="button"]), [er-window-content] input:not([type~="submit"]), ' +
					'[er-window-content] input:not([type~="reset"]), [er-window-content] input:not([type~="image"]), ' +
					'[er-window-content] select, [er-window-content] textarea, [er-window-content] meter';
				break;
			
			case ( selector == 'interactive' ):
				css_query = '[er-window-content] input, [er-window-content] select, [er-window-content] textarea, [er-window-content] button, ' +
					'[er-window-content] a, [er-window-content] area, [er-window-content] audio, [er-window-content] video, ' +
					'[er-window-content] meter, [er-window-content] details, [er-window-content] summary';
				break;
			
			case ( selector == 'none' ):
				return;
		}

		if ( !css_query )
			return;
		
		let focus_element = _window.value.querySelector( css_query );
		
		if ( !focus_element )
			return;
		
		setTimeout( () => focus_element.focus(), 15 );
	}


	function Get_element ()
	{
		return _window.value;
	}
	
	
	// endregion
	
	
	//region Private methods

	
	async function Trigger ( event_type )
	{
		const handler_fn = ref();
		const response = ref( { stop: false, message: null } );
		
		_emit( event_type, handler_fn );
		
		if ( handler_fn.value )
		{
			await handler_fn.value( _window, response );
			
			if ( response.value?.message )
				await Open_alert( response.value.message );
			
			if ( response.value.stop )
				return false;
		}
		
		return true;
	}


	async function Open_alert ( message )
	{
		_ui.value.alert.message = message;
		_ui.value.alert.is_attached = true;

		await nextTick();

		_alert.value.Open();
	}


	async function Show_spinner ()
	{
		if ( !_options.value.form.show_waiting )
			return;

		_ui.value.spinner.is_attached = true;

		await nextTick();

		_spinner.value.Open();
	}


	function Hide_spinner ()
	{
		if ( !_options.value.form.show_waiting )
			return;

		if ( _spinner.value )
			_spinner.value.Close();
		else
			Emergency.Fix_zombie_modals();

		// Unattach the window from the DOM after the animation (0.3s)
		setTimeout( () => _ui.value.spinner.is_attached = false, 350 );
	}
	
	
	async function Start_timeout ()
	{
		_ui.value.window.show_timeout = true;
		
		await nextTick();
		
		let counter = _window.value.querySelector( '[er-window-timeout] .count' );
		let timeout = _options.value.timeout.amount + 0;
		
		counter.innerHTML = timeout;
		
		_timeout_handler = setInterval( () =>
		{
			timeout--;
			
			if ( timeout >= 0 )
				counter.innerHTML = timeout;
			
			if ( timeout <= 0 )
				Close();
		}, 1000 );
	}
	
	
	function Cancel_timeout ()
	{
		if ( _timeout_handler )
			clearInterval( _timeout_handler );
		
		_timeout_handler = null;
		
		_ui.value.window.show_timeout = false;
	}
	
	
	function Autofocus ()
	{
		Focus( _options.value.main.autofocus );
	}
	
	
	// endregion

</script>

<style scoped>
	
	[er-window-handlers] .expand > *:only-child
	{
		margin: 0.05rem 0 0 0.05rem;
	}
	[er-window-handlers] .reduce > *:only-child
	{
		margin: -0.05rem 0 0 0.05rem;
	}

</style>